/** Переходы машины состояний. Компоненты зовут только эти функции. */

import type { LonLat } from '@app/geo'
import {
  accessVerified,
  buildGraph,
  type ClimbPref,
  planRoute,
  type RoutePlan,
  type RouteProfile,
  resolveStart,
  SPEED_MPS,
  type Venue,
} from '@app/rvf'
import { reportRouteBuilt } from './analytics'
import { importChunk } from './lazy-retry'
import type { NavigationSession, NavProgress } from './nav'
import { resetCameraHold } from './nav/camera-hold'
import { fetchExternalLeg, offSiteEntrance } from './route-splice'
import {
  type AccessMode,
  accessMode,
  activeLevelId,
  alertProfile,
  anchorId,
  announcedSteps,
  appState,
  arrivedAfterS,
  arSession,
  type ConnectorPref,
  cameraHeld,
  emergencyFrame,
  emergencyRouteLockTick,
  externalLeg,
  focusUnitId,
  geoStart,
  graph,
  guidancePref,
  headingUpOn,
  navProgress,
  navSteps,
  prefNoAltTick,
  routePlan,
  routePref,
  stepFreeOk,
  type TravelMode,
  targetId,
  travelMode,
  venue,
  venueMap,
  viewMode,
} from './state'
import { planCrossesLevels } from './ui/labels'

let session: NavigationSession | null = null
/** Поколение follow-сессии: гонко-защита асинхронного startFollow. */
let followGeneration = 0
let lastReplanAtMs = 0
let followStartedAtMs = 0
const REPLAN_MIN_INTERVAL_MS = 10_000

function targetExists(id: string): boolean {
  const v = venue.value
  if (!v) return false
  return (
    v.units.some((u) => u.id === id) ||
    v.pois.some((p) => p.id === id) ||
    v.network.nodes.some((n) => n.id === id)
  )
}

/**
 * Профиль графа. Раньше «лифт» подменял профиль на wheelchair — предпочтение
 * и потребность смешивались: выбравший лифт здоровый человек терял маршруты
 * через двери без пандуса. Теперь профиль задаёт ТОЛЬКО доступность, а выбор
 * лифта/лестницы — вес в графе (ClimbPref).
 */
function activeProfile(): RouteProfile {
  if (travelMode.value !== 'foot') return travelMode.value
  return accessMode.value === 'walk' ? 'foot' : 'wheelchair'
}

/** Пересобрать граф под предпочтение подъёма (микросекунды на наших сетях). */
function rebuildGraph(): void {
  const v = venue.value
  if (!v) return
  const map: Record<ConnectorPref, ClimbPref> = {
    lift: 'elevator',
    escalator: 'escalator',
    stairs: 'stairs',
  }
  const pref = routePref.value
  graph.value = buildGraph(v.network, pref ? { prefer: map[pref] } : {})
}

/**
 * Тап по объекту (карта/чип): маршрут считается И РИСУЕТСЯ сразу (фидбек
 * владельца 24.07) — внутренний расчёт локальный и бесплатный. Уличное плечо
 * внешних провайдеров (лимиты/ключи) — только по кнопке «Маршрут».
 */
/** Уровень цели: юнит или точка. null — уровней у площадки нет. */
function levelOfTarget(v: Venue, id: string): string | null {
  if (v.levels.length < 2) return null
  return (v.units.find((u) => u.id === id) ?? v.pois.find((p) => p.id === id))?.level ?? null
}

export function selectTarget(id: string): void {
  // Пользователь сам выбрал другую цель — «прожектор» на здании из ссылки
  // больше не нужен, карта возвращается к обычному виду.
  if (focusUnitId.value !== null && focusUnitId.value !== id) {
    focusUnitId.value = null
    venueMap.value?.setTargetFocus(null)
  }
  // ЧС: обычные маршруты заблокированы — красная дорога ведёт ТОЛЬКО к
  // укрытиям/выходам (решение владельца 26.07, анти-путаница). Тап по
  // обычному зданию показывает подсказку вместо маршрута.
  if (emergencyFrame.value) {
    emergencyRouteLockTick.value++
    return
  }
  const v = venue.value
  const g = graph.value
  if (!v || !g || !targetExists(id)) return
  // Вне площадки (гео дальше порога от сети) — внутренний маршрут строится
  // от ВХОДА, а уличное плечо до входа догружается цепочкой провайдеров (№23).
  const entrance = anchorId.value ? null : offSiteEntrance(v, geoStart.value)
  const start =
    entrance ??
    resolveStart(v, {
      anchorId: anchorId.value ?? undefined,
      geo: geoStart.value ?? undefined,
    })
  externalLeg.value = null
  venueMap.value?.setExternalLeg(null)
  const plan = start ? planRoute(v, g, start, id, activeProfile()) : null
  routePlan.value = plan
  // Чип «Можно без лестниц»: только когда маршрут реально меняет этаж.
  // «Можно без лестниц» — только когда доступность реально размечена: иначе мы
  // обещали бы человеку в коляске путь по непроверенным данным (замечание
  // эксперта по доступности к досье для города, 29.07).
  stepFreeOk.value =
    start && plan && planCrossesLevels(v, plan) && accessVerified(v.network.edges)
      ? planRoute(v, g, start, id, 'wheelchair') !== null
      : null
  targetId.value = id
  appState.value = 'target'
  const map = venueMap.value
  map?.highlight(id)
  // Цель на другом этаже — переключаем этаж, иначе она под фильтром уровня
  // невидима, и человек смотрит на пустую карту (фидбек владельца 28.07).
  const targetLevel = levelOfTarget(v, id)
  if (targetLevel && activeLevelId.value !== null && activeLevelId.value !== targetLevel) {
    activeLevelId.value = targetLevel
    map?.setLevel(targetLevel)
  }
  // И подъезжаем к выбранному: искать свой магазин глазами по всему центру —
  // не работа посетителя.
  map?.revealTarget(id)
  // Превью линии без затемнения и перелёта камеры — они по кнопке «Маршрут».
  map?.setRoute(plan)
}

/**
 * Уличное плечо до входа (№23, цепочка Valhalla/OSRM/…): лимиты провайдеров —
 * тянем ТОЛЬКО после явного «Маршрут», не на каждый тап по цели.
 */
function requestExternalLeg(targetIdAtCall: string): void {
  const v = venue.value
  if (!v || anchorId.value || externalLeg.value) return
  const geo = geoStart.value
  if (!geo) return
  const entrance = offSiteEntrance(v, geo)
  if (!entrance) return
  void fetchExternalLeg(geo, entrance.point).then((leg) => {
    // Пользователь мог сменить цель, пока плечо грузилось.
    if (targetId.value === targetIdAtCall) {
      externalLeg.value = leg
      venueMap.value?.setExternalLeg(leg?.polyline ?? null)
    }
  })
}

/** Смена способа подъёма: пересчитать активный маршрут, сохранив состояние. */
export function setConnectorPref(pref: ConnectorPref | null): void {
  if (routePref.value === pref) return
  routePref.value = pref
  // «Лестница» при включённом «без лестниц» — противоречие. Выбор человека
  // главнее: режим снимается, и это видно (чип гаснет + тост).
  if (pref === 'stairs' && accessMode.value !== 'walk') {
    accessMode.value = 'walk'
  }
  const before = routePlan.value?.nodeIds.join('|')
  rebuildGraph()
  replanCurrent()
  // Маршрут не изменился при явном выборе → на карте нет альтернативного
  // подъёма. Говорим это честно, иначе кнопка выглядит сломанной.
  const after = routePlan.value?.nodeIds.join('|')
  if (pref && before && before === after) prefNoAltTick.value++
}

/** Режим доступности: «без лестниц» (коляска инвалидная или детская). */
export function setAccessMode(mode: AccessMode): void {
  if (accessMode.value === mode) return
  accessMode.value = mode
  // Пешеходный режим не спорит с предпочтением; «без лестниц» несовместим
  // с предпочтением «лестница» — снимаем его.
  if (mode !== 'walk' && routePref.value === 'stairs') {
    routePref.value = null
    rebuildGraph()
  }
  replanCurrent()
}

/** Пересчитать активный маршрут, сохранив экранное состояние. */
function replanCurrent(): void {
  const id = targetId.value
  if (!id) return
  const wasRoute = appState.value === 'route'
  selectTarget(id)
  if (wasRoute) showRoute()
}

/** Смена способа передвижения (пешком/авто/фура): пересчёт маршрута. */
export function setTravelMode(mode: TravelMode): void {
  if (travelMode.value === mode) return
  travelMode.value = mode
  venueMap.value?.setRouteProfile(mode)
  const id = targetId.value
  if (!id) return
  const wasRoute = appState.value === 'route'
  selectTarget(id)
  if (wasRoute) showRoute()
}

export function closeTarget(): void {
  focusUnitId.value = null
  venueMap.value?.setTargetFocus(null)
  stopSession()
  externalLeg.value = null
  venueMap.value?.setExternalLeg(null)
  targetId.value = null
  routePlan.value = null
  appState.value = 'overview'
  const map = venueMap.value
  map?.highlight(null)
  map?.setRoute(null)
  map?.setDimmed(false)
  map?.fitVenue()
}

export function showRoute(): void {
  const plan = routePlan.value
  const map = venueMap.value
  if (!plan || !map) return
  appState.value = 'route'
  // Мультиэтажный маршрут начинаем с этажа СТАРТА (стандарт MapsIndoors):
  // человек стоит на нём; плечо цели — призраком, значок ведёт дальше.
  const firstLeg = plan.levelSplit.legs[0]
  if (firstLeg && plan.levelSplit.legs.length > 1 && activeLevelId.value !== firstLeg.level) {
    activeLevelId.value = firstLeg.level
    map.setLevel(firstLeg.level)
  }
  map.setRoute(plan)
  map.setDimmed(true)
  map.fitRoute(plan)
  requestExternalLeg(plan.targetId)
  // Анонимный агрегат venue × цель × час (ADR 0015) — без ID и координат.
  const v = venue.value
  if (v && plan.targetId) reportRouteBuilt(v.venue.id, plan.targetId)
}

export function backToTarget(): void {
  stopSession()
  const map = venueMap.value
  // Обратный путь (targetId пуст) не имеет карточки цели — назад значит к обзору.
  if (!targetId.value) {
    closeTarget()
    return
  }
  appState.value = 'target'
  // Превью-линия остаётся (рисуем маршрут сразу при выборе цели), уходит dim.
  map?.setRoute(routePlan.value)
  map?.setDimmed(false)
}

export async function startFollow(): Promise<void> {
  const plan = routePlan.value
  if (!plan || session) return
  // Ведение (MVP) идёт на плоской карте; изо-walk-режим — BACKLOG №10.
  viewMode.value = 'map'
  const generation = ++followGeneration
  const nav = await importChunk(() => import('./nav'))
  // Пока грузился чанк, пользователь мог выйти из маршрута или стартовать заново.
  // Допустимые исходные состояния: orient (штатный путь «Иду ✓»), route (прямой
  // старт), follow (молчаливое перепланирование при уходе с маршрута).
  const from = appState.value
  if (
    generation !== followGeneration ||
    (from !== 'orient' && from !== 'route' && from !== 'follow')
  )
    return
  navSteps.value = nav.extractSteps(plan.polyline)
  session = new nav.NavigationSession({
    polyline: plan.polyline,
    walkingSpeedMps: SPEED_MPS[plan.profile],
    startFallback: plan.start.point,
    onUpdate: (progress) => handleNavUpdate(progress),
  })
  appState.value = 'follow'
  // Новое ведение начинается с камеры у себя: удержание прошлой сессии
  // не должно молча выключать следование в новой.
  resetCameraHold()
  followStartedAtMs = Date.now()
  arrivedAfterS.value = null
  announcedSteps.clear()
  window.addEventListener('pagehide', onPageHide)
  venueMap.value?.startRouteDash()
  session.start()
  // Способ движения выбрал пользователь (спека 13): AR-оверлей поверх ведения.
  if (guidancePref.value === 'ar') arSession.value = 'follow'
}

function handleNavUpdate(progress: NavProgress): void {
  navProgress.value = progress
  // Голос/вибро по профилю подсказок (№5): поворот рядом или прибытие.
  void announceProgress(progress)
  const map = venueMap.value
  map?.setProgress(progress.matchedIndex)
  if (progress.position) {
    const kind = progress.positionSource === 'gps' ? 'gps' : 'anchor'
    map?.setYou(progress.position, progress.headingDeg, '', kind)
    // Камера едет за позицией, только если карту не держит человек: он мог
    // отодвинуть её пальцем, чтобы посмотреть вперёд (nav/camera-hold.ts).
    if (progress.positionSource === 'gps' && !cameraHeld.value) {
      map?.followCamera(progress.position, progress.headingDeg)
    }
  }
  if (progress.arrived) {
    finishFollow()
    return
  }
  if (progress.offRoute && progress.position) replanFrom(progress.position)
}

/** Анонс поворота/прибытия выбранным профилем (лениво — чанк nav/alerts). */
async function announceProgress(progress: NavProgress): Promise<void> {
  if (alertProfile.value === 'silent') return
  const alerts = await importChunk(() => import('./nav/alerts'))
  const ann = alerts.nextAnnouncement(
    progress,
    navSteps.value,
    announcedSteps,
    progress.distanceDoneM,
  )
  if (!ann) return
  announcedSteps.add(ann.type === 'turn' ? ann.step.index : -1)
  alerts.deliver(ann, alertProfile.value)
}

/** Молчаливое перепланирование при уходе с маршрута (без красных баннеров). */
function replanFrom(position: LonLat): void {
  const v = venue.value
  const g = graph.value
  const id = targetId.value
  if (!v || !g || !id) return
  const now = Date.now()
  if (now - lastReplanAtMs < REPLAN_MIN_INTERVAL_MS) return
  lastReplanAtMs = now
  const start = resolveStart(v, { geo: position })
  const plan = start ? planRoute(v, g, start, id) : null
  if (!plan) return
  // AR-оверлей и «по курсу» переживают тихий replan: движок AR сам подхватит
  // новую полилинию (engine.syncRoute), камера не мигает, режим не сбрасывается.
  stopSession(true)
  routePlan.value = plan
  venueMap.value?.setRoute(plan)
  void startFollow()
}

/**
 * Смена цели прямо в пути (тап по подписи объекта в AR): маршрут пересобирается,
 * ведение продолжается, AR-оверлей и «карта по курсу» не гаснут — камера не
 * мигает, режим и привязка компаса сохраняются.
 */
export function retargetFollow(id: string): boolean {
  if (appState.value !== 'follow' || !targetExists(id)) return false
  selectTarget(id)
  const plan = routePlan.value
  if (!plan) return false
  stopSession(true)
  // selectTarget оставил экран в состоянии ЦЕЛЬ. Ставим ведение СРАЗУ (а не
  // через МАРШРУТ): иначе на один тик панель ведения размонтируется и шаги
  // степпера сбрасываются. startFollow принимает 'follow' как исходное.
  appState.value = 'follow'
  venueMap.value?.setDimmed(true)
  const v = venue.value
  if (v && plan.targetId) reportRouteBuilt(v.venue.id, plan.targetId)
  void startFollow()
  return true
}

/** «Дальше ✓» степпера: без GPS прогресс двигает пользователь. */
export function advanceToPolylineIndex(index: number, done: boolean): void {
  const map = venueMap.value
  map?.setProgress(index)
  // Честный маркер «по отметкам»: примерная позиция — пунктиром (System).
  const point = routePlan.value?.polyline[index]
  if (point) map?.setYou(point, null, '', 'steps')
  if (done) finishFollow()
}

function finishFollow(): void {
  if (followStartedAtMs > 0) {
    arrivedAfterS.value = Math.round((Date.now() - followStartedAtMs) / 1000)
    followStartedAtMs = 0
  }
  stopSession()
  appState.value = 'arrived'
}

export function stopFollow(): void {
  closeTarget()
}

/** «Обратный путь»: тот же маршрут назад; старт — точка прибытия (не якорь!). */
export function startBackRoute(): void {
  const plan = routePlan.value
  const map = venueMap.value
  if (!plan || !map) return
  const polyline = [...plan.polyline].reverse()
  const startPoint = polyline[0]
  if (!startPoint) return
  const reversed: RoutePlan = {
    ...plan,
    targetId: '',
    polyline,
    start: { point: startPoint, nodeId: plan.start.nodeId, source: 'anchor' },
  }
  targetId.value = null
  routePlan.value = reversed
  navProgress.value = null
  appState.value = 'route'
  map.setRoute(reversed)
  map.setDimmed(true)
  map.fitRoute(reversed)
}

function onPageHide(): void {
  stopSession()
}

/** keepAr — тихий replan: AR-сессия и «по курсу» продолжают жить. */
function stopSession(keepAr = false): void {
  followGeneration++
  window.removeEventListener('pagehide', onPageHide)
  venueMap.value?.stopRouteDash()
  session?.stop()
  session = null
  navProgress.value = null
  navSteps.value = []
  if (keepAr) return
  if (arSession.value === 'follow') arSession.value = null
  // «Карта по курсу» живёт только в ведении; чанк уже загружен — импорт из кеша.
  if (headingUpOn.value) void importChunk(() => import('./ar')).then((m) => m.stopHeadingUp())
}
